import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import domtoimage from "dom-to-image";
import JSZip from "jszip";
import {
  Box,
  Stack,
  Text,
  UnstyledButton,
  Title,
  Paper,
  Divider,
  Group,
  Switch,
  MultiSelect,
  SegmentedControl,
} from "@mantine/core";
import { createStyles, rem, useMantineColorScheme } from "@mantine/styles";
import { useTranslation, Trans } from "react-i18next";
import { ChartSkeleton } from "../Skeleton";
import ResultSeries from "../timeSeriesGraph/resultSeries";
import ResultSeriesThermal from "../timeSeriesGraph/resultSeriesThermal";
import EnergyBalance from "./energyBalanceTab";
import Synthesis from "./synthesis";
import CashFlowTable from "./cashflowTable";
import CashFlowChart from "./cashFlowChart";
import AssetCost from "./assetTab/assetCost";
import ScenarioTable from "./scenario";
import HighlightsIndicator from "./highlights/highlightsIndicator";
import EnergyFlow from "./EnergyFlowDiagram";
import MicrogridInteractiveSankey from "./MicrogridInteractiveSankey";
import { useAtom } from "jotai";
import { projectDetailsAtom } from "../../utils/atoms/projectDetailsAtom";
import PropTypes from "prop-types";
import { useIsLightMode, formatCompactNumber } from "../../utils/helpers";
import { ReactComponent as accumulatorIcon } from "./flowAsset/accumulator.svg";
import { ReactComponent as electricGeneratorIcon } from "./flowAsset/electric-generator.svg";
import { ReactComponent as pylonIcon } from "./flowAsset/pylon.svg";
import { ReactComponent as solarCell } from "./flowAsset/solar-cell.svg";
import { formatValueToPercentage } from "./RESULT_CONFIG";
import BillSummaryChart from "./billSummary";
import BillSummaryTable from "./BillSummaryTable";
import { useAssessmentRouteParams, isValidateDataForEnergyFlow } from "../../constants/helper";
import MGAAPI from "../../api/index";
import fileDownload from "js-file-download";
import displayError from "../../constants/utils";
import SEConfirmModal from "../common/SEConfirmModal";
import { SeLoading } from "@se/web-ui-react";
import env from "../../env";

const useStyles = createStyles((theme) => ({
  container: {
    display: "flex",
    height: "100vh",
    width: "100%",
    minHeight: "100vh",
  },

  sidebar: {
    borderLeft: `1px solid ${theme.colors.gray[3]}`,
    padding: rem(10),
    position: "sticky",
    height: "max-content",
  },

  contentWrapper: {
    flex: 1,
    height: "100vh !important",
    overflowY: "auto",
    paddingInline: rem(32),
    paddingBottom: "10%",
    scrollbarWidth: "none",
  },

  tocItem: {
    padding: `${rem(8)} ${rem(12)}`,
    borderRadius: theme.radius.sm,
    cursor: "pointer",
    fontSize: theme.fontSizes.sm,
    transition: "background-color 150ms ease",
    "&:hover": {
      // backgroundColor: theme.colors.dark[7],
    },
  },

  activeTocItem: {
    backgroundColor: theme.colors.blue[0],
    color: theme.colors.blue[7],
    fontWeight: 600,
  },

  section: {
    marginBottom: rem(64),
  },
}));
const MGA_API = new MGAAPI();

const TOCStickyLayout = forwardRef(
  (
    {
      resultTimeSeries,
      resultSimulationData,
      showCompleteMicrogrid,
      showScenario,
      handleClickCompare,
      simulationData,
      selectedValue,
      selectedMicrogridId,
      kpiFactorsData,
      kpiProjectSettings,
    },
    ref
  ) => {
    const { t } = useTranslation();
    // Expose handleDownloadSections to parent via ref
    const [isDownloading, setIsDownloading] = useState(false);
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const { classes, cx } = useStyles();
    const [projectDetails] = useAtom(projectDetailsAtom);
    const sectionRefs = useRef({});
    const [showBillChart, setShowBillChart] = useState(true);
    const { designVersionId, designId, projectId } = useAssessmentRouteParams();
    // Download all visible sections as PNG images, then request a server-generated report.
    // format: 'pdf' | 'docx'
    const handleDownloadSections = async (format = "pdf") => {
      setIsDownloading(true);
      const visibleSections = sections?.filter(
        (section) =>
          (section?.id === "timeSeries" || section?.isDataPresent) &&
          sectionRefs.current[section?.id]
      );
      const zip = new JSZip();
      for (const section of visibleSections) {
        if (section?.id) {
          let node1 = sectionRefs.current[section?.id];
          const node = node1.getElementsByClassName(
            "indicator-downloadable"
          )?.[0];
          if (node) {
            // Save original class and style
            const originalClass = node.className;
            const originalStyle = node.getAttribute("style") || "";
            const originalTransform = node.style.transform;
            const originalTransformOrigin = node.style.transformOrigin;
            const originalPadding = node.style.padding;
            // Save parent zoom/scale
            const parent = node.parentElement;
            const originalParentZoom = parent && parent.style.zoom;
            const originalParentTransform = parent && parent.style.transform;
            try {
              // node.classList.add("force-light-mode-download");
              // if (parent) {
              //   parent.style.zoom = "1";
              //   parent.style.transform = "scale(1)";
              // }
              // node.style.padding = "0";
              // node.style.transformOrigin = "top left";
              await new Promise(requestAnimationFrame);
              const dataUrl = await domtoimage.toPng(node);
              // Add PNG to zip
              zip.file(
                `${section.name || section.id}.png`,
                dataUrl.split(",")[1],
                { base64: true }
              );
            } catch (error) {
              console.error(
                `Failed to download section ${section.label}:`,
                error
              );
            }
            // } finally {
            //   node.className = originalClass;
            //   node.setAttribute("style", originalStyle);
            //   node.style.transform = originalTransform;
            //   node.style.transformOrigin = originalTransformOrigin;
            //   node.style.padding = originalPadding;
            //   if (parent) {
            //     parent.style.zoom = originalParentZoom;
            //     parent.style.transform = originalParentTransform;
            //   }
            // }
          }
        }
      }
      // Add description.json to zip
      const descriptionJson = {
        highlights: [
          document.getElementById("indicator-highlight-1")?.innerText || "",
          document.getElementById("indicator-highlight-2")?.innerText || "",
        ],
        energyFlow: [
          document.getElementById("indicator-energy-1")?.innerText || "",
          document.getElementById("indicator-energy-2")?.innerText || "",
          document.getElementById("indicator-energy-3")?.innerText || "",
        ],
        energyBalance: [
          document.getElementById("indicator-balance-1")?.innerText || "",
          document.getElementById("indicator-balance-2")?.innerText || "",
          document.getElementById("indicator-balance-3")?.innerText || "",
        ],
        billSummary: [
          document.getElementById("indicator-bill-1")?.innerText || "",
          document.getElementById("indicator-bill-2")?.innerText || "",
          document.getElementById("indicator-bill-3")?.innerText || "",
          document.getElementById("indicator-bill-4")?.innerText || "",
          document.getElementById("indicator-bill-5")?.innerText || "",
          document.getElementById("indicator-bill-6")?.innerText || "",
          document.getElementById("indicator-bill-7")?.innerText || "",
        ],
      };
      zip.file("description.json", JSON.stringify(descriptionJson, null, 2));

      // Generate and download zip
      zip
        .generateAsync({
          type: "blob",
          compression: "DEFLATE",
          compressionOptions: { level: 9 },
        })
        .then((blob) => {
          // Download locally
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          // a.href = url;
          // a.download = "result_images.zip";
          // document.body.appendChild(a);
          // a.click();
          // setTimeout(() => {
          //   document.body.removeChild(a);
          //   URL.revokeObjectURL(url);
          // }, 100);

          // Convert Blob to File for upload
          const file = new File([blob], "result_images.zip", {
            type: "application/zip",
          });
          MGA_API.uploadResultImages(
            projectId,
            designId,
            designVersionId,
            selectedMicrogridId, // scenarioId
            file
          )
            .then(() => {
              MGA_API.report(
                projectId,
                designId,
                designVersionId,
                selectedMicrogridId,
                format,
                {},
                selectedValue
              )
                .then((res) => {
                  const safeFormat = format === "docx" ? "docx" : "pdf";
                  fileDownload(res, `Report.${safeFormat}`);
                  setIsDownloading(false);

                  displayError("success", t("result.download.success"));
                })
                .catch((error) => {
                  setIsDownloading(false);

                  console.log("Error Downloading File:", error);
                });
            })
            .catch((error) => {
              console.log(error);
              setIsDownloading(false);
            });
        });
    };

    const contentRef = useRef();
    const [isAssetChecked, setIsAssetChecked] = useState(false);
    const [assetView, setAssetView] = useState("chart");
    const { colorScheme } = useMantineColorScheme();
    const [billSummaryView, setBillSummaryView] = useState("chart");
    const [isCashflowDiscounted, setIsCashflowDiscounted] = useState(false);
    const [activeId, setActiveId] = useState("highlights");
    const isLightMode = useIsLightMode();
    const [enabledAssets, setEnabledAssets] = useState([]);
    const [showTimeSeriesChart, setShowTimeSeriesChart] = useState(false);
    const [timeSeriesLayer, setTimeSeriesLayer] = useState("electrical");
    const timeSeriesSectionRef = useRef(null);
    const isDevEnv = useMemo(() => {
      const apiUrl = (env?.REACT_APP_MGA_BACKEND_API_URL || "").toLowerCase();
      const host = (window?.location?.hostname || "").toLowerCase();
      return host.includes("localhost") || apiUrl.includes("dev") || apiUrl.includes("int");
    }, []);

    const normalizeAssetKey = useCallback(
      (assetName) => `${assetName ?? ""}`.toLowerCase().replace(/[\s_-]/g, ""),
      []
    );

    const isIgnoredAsset = useCallback(
      (assetName) => {
        const normalized = normalizeAssetKey(assetName);
        return normalized === "building" || normalized === "heatsource";
      },
      [normalizeAssetKey]
    );

    useImperativeHandle(ref, () => ({
      handleDownloadSections,
    }));

    // Energy flow metrics (aligned with EnergyFlowDiagram.js)
    const loadElectrical =
      resultSimulationData?.energyBalance?.componentsAttributes?.load
        ?.electrical || 0; // total consumption (kWh)
    const gridImport =
      resultSimulationData?.energyBalance?.componentsAttributes?.utility
        ?.import || 0; // buying (kWh)
    const gridExport =
      resultSimulationData?.energyBalance?.componentsAttributes?.utility
        ?.export || 0; // export (kWh)
         const selfConsumptionKWhead =
      (resultSimulationData?.energyBalance?.componentsAttributes?.pv
        ?.electrical || 0) +
      (resultSimulationData?.energyBalance?.componentsAttributes?.genset
        ?.electrical || 0);
    const selfProductionKWh =
      (resultSimulationData?.energyBalance?.componentsAttributes?.battery
        ?.batteryLoss || 0) +
      (resultSimulationData?.energyBalance?.componentsAttributes?.pv
        ?.electrical || 0) +
      (resultSimulationData?.energyBalance?.componentsAttributes?.genset
        ?.electrical || 0); // on-site generation used (kWh)
      const selfConsumptionKWh = selfProductionKWh - gridExport; // on-site generation consumed locally (kWh)

    const totalConsumption = loadElectrical; // per statement definition
    const gridSharePct = totalConsumption
      ? ((gridImport / totalConsumption) * 100)?.toFixed(2)
      : 0; // A
    const onsiteSharePct = 100 - gridSharePct; // 100 - A

    const totalGenerationKWh =  Math.abs(selfProductionKWh); // B = self production + export
    const totalGenerationUsedKWh = Math.abs(selfConsumptionKWhead); // on-site generation used locally (kWh)
    const localUsePct = totalGenerationKWh
      ? Math.abs(((selfConsumptionKWh / totalGenerationKWh) * 100)?.toFixed(2))
      : 0; // self production / B * 100
    const exportPct = totalGenerationKWh
      ? Math.abs(Math.round((gridExport / totalGenerationKWh) * 100))
      : 0; // export / B * 100

    // handleChange removed - using SegmentedControl with direct value binding instead

    useEffect(() => {
      const allAssetNames = Array.from(
        new Set(resultSimulationData?.assetsInfo?.map((a) => a?.assetName))
      );
      setEnabledAssets(allAssetNames?.filter((name) => !isIgnoredAsset(name)));
    }, [resultSimulationData, isIgnoredAsset]);

    const selfProductionData =
      resultSimulationData?.energyBalance?.componentsAttributes?.battery
        ?.batteryLoss +
      resultSimulationData?.energyBalance?.componentsAttributes?.pv
        ?.electrical +
      resultSimulationData?.energyBalance?.componentsAttributes?.genset
        ?.electrical;

    const sumCashflowTotal = useCallback((cashflow = []) => {
      return (cashflow || [])?.reduce((total, yearEntry) => {
        const values = yearEntry?.nominalValue || {};
        const rowTotal = Object.values(values).reduce((rowSum, value) => {
          const numericValue = Number(value);
          return Number.isFinite(numericValue) ? rowSum + numericValue : rowSum;
        }, 0);
        return total + rowTotal;
      }, 0);
    }, []);

    const selectedSimulationData = useMemo(
      () =>
        selectedValue === "referenceExistingMicrogrid"
          ? simulationData?.completeMicrogrid
          : simulationData?.[selectedValue] || simulationData?.completeMicrogrid,
      [simulationData, selectedValue]
    );

    const cashflowIndicatorData = useMemo(() => {
      const referenceTotal = sumCashflowTotal(
        simulationData?.referenceExistingMicrogrid?.cashflow
      );

      const selectedTotal = sumCashflowTotal(selectedSimulationData?.cashflow);

      return {
        preCashflowTotal: referenceTotal,
        postCashflowTotal: selectedTotal,
      };
    }, [simulationData, selectedSimulationData, sumCashflowTotal]);

    const derInfo = useMemo(
      () => selectedSimulationData?.derInfoDTO || {},
      [selectedSimulationData]
    );

    const iconKpiItems = useMemo(() => {
      // If derInfoDTO is absent, show nothing
      if (!derInfo || Object.keys(derInfo)?.length === 0) return [];

      // Input is kW → convert to W → pick SI unit → format as plain number
      const formatPower = (kw) => {
        const watt = parseFloat(kw) * 1000;
        if (!Number.isFinite(watt)) return { value: "—", unit: "W" };
        const abs = Math.abs(watt);
        const fmt = (n) => parseFloat(n.toPrecision(4)).toLocaleString("en-US");
        if (abs < 1e3)  return { value: fmt(watt),          unit: "W"  };
        if (abs < 1e6)  return { value: fmt(watt / 1e3),    unit: "kW" };
        if (abs < 1e9)  return { value: fmt(watt / 1e6),    unit: "MW" };
        if (abs < 1e12) return { value: fmt(watt / 1e9),    unit: "GW" };
        return                 { value: fmt(watt / 1e12),   unit: "TW" };
      };
      return [
        ...(derInfo.Grid ? [{
          icon: pylonIcon,
          alt: "Pylon",
          label: "Grid",
          value: (() => { const r = formatPower(derInfo?.Grid?.MaxImport); return <span>Maximum Import <strong >{r.value}</strong> {r.unit}</span>; })(),
          value2: (() => { const r = formatPower(derInfo?.Grid?.MaxExport); return <span>Maximum Export <strong >{r.value}</strong> {r.unit}</span>; })(),
        }] : []),
        ...(derInfo?.PV ? [{
          icon: solarCell,
          alt: "Solar Cell",
          label: "Photo Voltaic",
          value: (() => { const r = formatPower(derInfo?.PV?.Power); return <span>Power <strong>{r.value}</strong> {r.unit}</span>; })(),
          value2: null,
        }] : []),
        ...(derInfo?.Bess ? [{
          icon: accumulatorIcon,
          alt: "Accumulator",
          label: "BESS",
          value: (() => { const r = formatPower(derInfo?.Bess?.ChargePower); return <span>Nominal Charge Power <strong>{r.value}</strong> {r.unit}</span>; })(),
          value2: (() => { const r = formatPower(derInfo?.Bess?.DischargePower); return <span>Nominal Discharge Power <strong>{r.value}</strong> {r.unit}</span>; })(),
          value3: (() => { const r = formatPower(derInfo?.Bess?.Energy); return <span>Energy <strong>{r.value}</strong> {r.unit}h</span>; })(),
        }] : []),
        ...(derInfo?.Genset ? [{
          icon: electricGeneratorIcon,
          alt: "Genset",
          label: "Genset",
          value: (() => { const r = formatPower(derInfo?.Genset?.Power); return <span>Power <strong>{r.value}</strong> {r.unit}</span>; })(),
          value2: null,
        }] : []),
      ];
    }, [derInfo]);

    const highlightsSectionStyle = useMemo(
      () => ({
        display: "flex",
        flexDirection: "column",
        zoom: "118%",
        border: "red",
        backgroundColor: !isLightMode && "var(--se-background-alternative)",
      }),
      [isLightMode]
    );

    const highlightsSectionChildren = useMemo(
      () =>
        resultSimulationData?.synthesis && !showCompleteMicrogrid ? (
          <div
            className="indicator-downloadable samr"
            style={highlightsSectionStyle}
          >
            <HighlightsIndicator
              data={resultSimulationData?.synthesis}
              scenarioData={resultSimulationData?.scenario}
              showCompleteMicrogrid={showCompleteMicrogrid}
              kpiFactorsData={kpiFactorsData}
              cashflowIndicatorData={cashflowIndicatorData}
              selectedSimulation={selectedSimulationData}
            />
          </div>
        ) : (
          t("common.noDataAvailable")
        ),
      [
        resultSimulationData?.synthesis,
        resultSimulationData?.scenario,
        cashflowIndicatorData,
        highlightsSectionStyle,
        showCompleteMicrogrid,
        selectedSimulationData,
      ]
    );

    const energyFlowContent = resultSimulationData?.energyFlow ? (
      isValidateDataForEnergyFlow(resultSimulationData?.energyFlow) ? (
        <MicrogridInteractiveSankey
          energyFlow={resultSimulationData?.energyFlow}
        />
      ) : (
        t("common.noDataConfiguration")
      )
    ) : (
      <EnergyFlow
        data={resultSimulationData?.energyBalance}
        scenario={resultSimulationData?.scenario}
      />
    );

    const sections = useMemo(
      () => [
        {
          id: "highlights",
          name: "HighLights",
          label: t("result.toc.section.highlights"),
          isDataPresent: resultSimulationData?.synthesis,
          description:
            t("result.highlights.description"),
          children: highlightsSectionChildren,
        },
        {
          id: "energyFlow",
          name: "EnergyFlow",
          label: t("result.toc.section.energyFlow"),
          isDataPresent: resultSimulationData?.energyBalance,
          description:
            t("result.energyFlow.description"),
          children: resultSimulationData?.energyBalance ? (
            <div className="mt-3 d-flex flex-column ">
              <span id="indicator-energy-1" style={{ display: "none" }}>
                {gridSharePct}
              </span>
              <span id="indicator-energy-2" style={{ display: "none" }}>
                {onsiteSharePct}
              </span>
              <span id="indicator-energy-3" style={{ display: "none" }}>
                {localUsePct}
              </span>
              <span id="indicator-energy-4" style={{ display: "none" }}>
                {exportPct}
              </span>
              <span className="indicator-downloadable">{energyFlowContent}</span>
            </div>
          ) : (
            t("common.noDataAvailable")
          ),
        },
        {
          key: "energyBalance",
          id: "energyBalance",
          name: "EnergyBalance",
          label: t("result.toc.section.energyBalance"),
          description: t("result.energyBalance.description", {
            defaultValue: `The system supplies an annual energy demand of ${resultSimulationData?.energyBalance?.componentsAttributes?.load?.electrical} MWh with ${resultSimulationData?.energyBalance?.componentsAttributes?.utility?.import} MWh from the Utility and ${formatValueToPercentage(selfProductionData).toFixed(2)} MWh from PV, BESS, and Genset sources. BESS usage includes charging, discharging, and losses, and there is no excess or unmet energy.`,
            loadElectrical: resultSimulationData?.energyBalance?.componentsAttributes?.load?.electrical,
            utilityImport: resultSimulationData?.energyBalance?.componentsAttributes?.utility?.import,
            selfProduction: formatValueToPercentage(selfProductionData)?.toFixed(2)
          }),
          isDataPresent: resultSimulationData?.energyBalance,
          children: resultSimulationData?.energyBalance ? (
            <>
              <span id="indicator-balance-1" style={{ display: "none" }}>
                {
                  resultSimulationData?.energyBalance?.componentsAttributes
                    ?.load?.electrical
                }
              </span>
              <span id="indicator-balance-2" style={{ display: "none" }}>
                {
                  resultSimulationData?.energyBalance?.componentsAttributes
                    ?.utility?.import
                }
              </span>
              <span id="indicator-balance-3" style={{ display: "none" }}>
                {formatValueToPercentage(selfProductionData).toFixed(2)}
              </span>
              <EnergyBalance
                energyBalance={resultSimulationData?.energyBalance}
              />
            </>
          ) : (
            t("common.noDataAvailable")
          ),
        },
        {
          key: "synthesis",
          id: "synthesis",
          name: "Synthesis",
          label: t("result.toc.section.synthesis"),
          isDataPresent: resultSimulationData?.synthesis,
          children: resultSimulationData?.synthesis ? (
            <Synthesis data={resultSimulationData?.synthesis} />
          ) : (
            t("common.noDataAvailable")
          ),
        },
        {
          key: "timeSeries",
          id: "timeSeries",
          name: "TimeSeries",
          label: t("result.toc.section.timeSeries"),
          description: t("result.timeSeries.description"),
          isDataPresent: resultTimeSeries?.load?.length,
          children: (() => {
            const hasThermal =
              (resultTimeSeries?.thermalBefore?.length || 0) > 0 ||
              (resultTimeSeries?.thermalAfter?.length || 0) > 0;
            if (!resultTimeSeries?.load?.length) {
              return <ChartSkeleton />;
            }
            return (
              <div className="indicator-downloadable">
                {hasThermal && (
                  <Group position="center" align="center" mb="md">
                    <SegmentedControl
                      value={timeSeriesLayer}
                      onChange={setTimeSeriesLayer}
                      data={[
                        { label: t("result.timeSeries.electrical"), value: "electrical" },
                        { label: t("result.timeSeries.thermal"), value: "thermal" },
                      ]}
                      color="seGreen.0"
                    />
                  </Group>
                )}
                {!hasThermal || timeSeriesLayer === "electrical" ? (
                  <ResultSeries results={resultTimeSeries} kpiProjectSettings={kpiProjectSettings} />
                ) : (
                  <ResultSeriesThermal
                    results={{
                      thermalBefore: resultTimeSeries?.thermalBefore,
                      thermalAfter: resultTimeSeries?.thermalAfter,
                    }}
                    kpiProjectSettings={kpiProjectSettings}
                  />
                )}
              </div>
            );
          })(),
        },
        {
          key: "cashFlow",
          id: "cashFlow",
          name: "CashFlow",
          label: t("result.toc.section.cashFlow"),
          isDataPresent: resultSimulationData?.cashflow?.length,
          description:
            t("result.cashFlow.description"),
          children: resultSimulationData?.cashflow?.length ? (
            <>
              <Group position="center" align="center" mb="md">
                <SegmentedControl
                  value={billSummaryView}
                  onChange={setBillSummaryView}
                    data={[
                    { label: t("common.chart"), value: "chart" },
                    { label: t("common.table"), value: "table" },
                  ]}
                  color="seGreen.0"
                />
              </Group>
              <Group position="center" spacing="md" mb="md">
                <Text>{t("common.nominal")}</Text>
                <Switch
                  checked={isCashflowDiscounted}
                  onChange={(e) => setIsCashflowDiscounted(e.currentTarget.checked)}
                  color="seGreen.0"
                />
                <Text>{t("common.discounted")}</Text>
              </Group>
              {billSummaryView === "chart" ? (
                <CashFlowChart data={resultSimulationData?.cashflow} isDiscounted={isCashflowDiscounted} />
              ) : (
                <CashFlowTable resultSimulationData={resultSimulationData} isDiscounted={isCashflowDiscounted} />
              )}
            </>
          ) : (
            t("common.noDataAvailable")
          ),
        },
        {
          id: "billSummary",
          name: "BillSummary",
          label: t("result.toc.section.billSummary"),
          description: (() => {
            // Consumption drop
            const referenceData = simulationData?.referenceExistingMicrogrid;
            const scenarioData =
              selectedValue === "referenceExistingMicrogrid"
                ? simulationData?.completeMicrogrid
                : simulationData?.[selectedValue] ||
                  simulationData?.completeMicrogrid;
            const preCost =
              referenceData?.energyBalance?.tableData?.preMicrogridCost ?? 0;
            const postCost =
              scenarioData?.energyBalance?.tableData?.postMicrogridCost ?? 0;
            const percentDrop = postCost
              ? ((preCost - postCost) / preCost) * 100
              : 0;

            // Peak demand drop
            const beforeDemandArr =
              referenceData?.billSummaryDTO?.map((b) => b?.demand) ?? [];
            const afterDemandArr =
              scenarioData?.billSummaryDTO?.map((b) => b?.demand) ?? [];
            const beforeDemandSum = beforeDemandArr?.reduce(
              (a, b) => a + (b || 0),
              0
            );
            const afterDemandSum = afterDemandArr?.reduce(
              (a, b) => a + (b || 0),
              0
            );
            const percentPeakDrop = afterDemandSum
              ? ((beforeDemandSum - afterDemandSum) / beforeDemandSum) * 100
              : 0;

            // Monthly cost drop (find minimum drop across months)
            const beforeChargesArr =
              referenceData?.billSummaryDTO?.map((b) => b?.totalCharges) ?? [];
            const afterChargesArr =
              scenarioData?.billSummaryDTO?.map((b) => b?.totalCharges) ?? [];
            let monthlyDrops = [];
            for (
              let i = 0;
              i < beforeChargesArr?.length && i < afterChargesArr?.length;
              i++
            ) {
              const before = beforeChargesArr?.[i] || 0;
              const after = afterChargesArr?.[i] || 0;
              if (before) {
                monthlyDrops?.push(((before - after) / before) * 100);
              }
            }
            const percentMonthlyDrop =
              monthlyDrops?.length > 0 ? Math.min(...monthlyDrops) : 0;

            // Charges range
            const beforeChargesMin = Math.min(
              ...beforeChargesArr?.filter((x) => typeof x === "number")
            );
            const beforeChargesMax = Math.max(
              ...beforeChargesArr?.filter((x) => typeof x === "number")
            );
            const afterChargesMin = Math.min(
              ...afterChargesArr?.filter((x) => typeof x === "number")
            );
            const afterChargesMax = Math.max(
              ...afterChargesArr?.filter((x) => typeof x === "number")
            );

            // Format numbers
            const fmt = (n, digits = 0) =>
              isFinite(n) ? n.toFixed(digits) : "--";
            // Helper for color
            const colorText = (value, text) => {
              const color =
                value > 0 ? "#3dcd58" : value < 0 ? "red" : "inherit";
              return `<span style='color:${color};font-weight:600'>${text}</span>`;
            };
            // Consumption drop
            const dropText = colorText(
              percentDrop,
              `${fmt(percentDrop, 0)}%`
            );
            // Peak demand drop
            const peakText = colorText(
              percentPeakDrop,
              `${fmt(percentPeakDrop, 0)}%`
            );
            // Monthly cost drop
            const monthlyText = colorText(
              percentMonthlyDrop,
              `~${fmt(percentMonthlyDrop, 0)}%`
            );

            // Charges range

            const beforeMinText = `$${fmt(beforeChargesMin, 0)}`;
            const beforeMaxText = `$${fmt(beforeChargesMax, 0)}`;

            // Green if after < before, else red if after > before, inherit if equal
            const afterMinText = `<span style='color:${
              afterChargesMin < beforeChargesMin
                ? "#3dcd58"
                : afterChargesMin > beforeChargesMin
                ? "red"
                : "inherit"
            };font-weight:600'>$${fmt(afterChargesMin, 0)}</span>`;
            const afterMaxText = `<span style='color:${
              afterChargesMax < beforeChargesMax
                ? "#3dcd58"
                : afterChargesMax > beforeChargesMax
                ? "red"
                : "inherit"
            };font-weight:600'>$${fmt(afterChargesMax, 0)}</span>`;

            // Render hidden spans for export, but return HTML string for description
            setTimeout(() => {
              // Ensure spans are in DOM for export
              const container = document.getElementById("indicator-bill-show");
              if (container) {
                container.innerHTML = `
                <span id="indicator-bill-1" style="display:none">${fmt(percentDrop, 0)}</span>
                <span id="indicator-bill-2" style="display:none">${fmt(percentPeakDrop,0)}</span>
                <span id="indicator-bill-3" style="display:none">${fmt(percentMonthlyDrop,0)}</span>
                <span id="indicator-bill-4" style="display:none">${fmt(beforeChargesMin,0)}</span>
                <span id="indicator-bill-5" style="display:none">${fmt(beforeChargesMax,0)}</span>
                <span id="indicator-bill-6" style="display:none">${fmt(afterChargesMin,0)}</span>
                <span id="indicator-bill-7" style="display:none">${fmt(afterChargesMax,0)}</span>
              `;
              }
            }, 0);
            return t("result.billSummary.descriptionText", {
              defaultValue:`Based on a 12-month analysis, the Microgrid proposal results in a ${monthlyText} shift in overall Utility Bill. This shift is achieved through a ${dropText} changes in energy consumption and a ${peakText} adjustment in peak demand requirements`,
              dropText,
              peakText,
              monthlyText,
              beforeMinText,
              beforeMaxText,
              afterMinText,
              afterMaxText,
            });
          })(),
          isDataPresent: true, // Always show for now, can be made conditional
          children: (() => {
            const beforeBillSummary =
              simulationData?.referenceExistingMicrogrid?.billSummaryDTO;

            const afterBillSummary =
              selectedValue === "referenceExistingMicrogrid"
                ? simulationData?.completeMicrogrid?.billSummaryDTO
                : simulationData?.[selectedValue]?.billSummaryDTO ||
                  simulationData?.completeMicrogrid?.billSummaryDTO;

            return (
              <>
              <div id="indicator-bill-show"></div>
                <Group
                  position="center"
                  align="center"
                  mb="md"
                  
                >
                  <SegmentedControl
                    value={showBillChart ? "chart" : "table"}
                    onChange={(value) => setShowBillChart(value === "chart")}
                    data={[
                      { label: t("common.chart"), value: "chart" },
                      { label: t("common.table"), value: "table" },
                    ]}
                    color="seGreen.0"
                  />
                </Group>
                <div>
                  {showBillChart ? (
                    <BillSummaryChart
                      beforeMG={beforeBillSummary}
                      afterMG={selectedValue === "referenceExistingMicrogrid"? null :afterBillSummary}
                    />
                  ) : (
                    <BillSummaryTable
                      beforeMG={beforeBillSummary}
                      afterMG={afterBillSummary}
                      showComparison={selectedValue !== "referenceExistingMicrogrid"}
                    />
                  )}
                </div>
              </>
            );
          })(),
        },
        {
          key: "assets",
          id: "assets",
          name: "Assets",
          label: t("result.toc.section.assets"),
          description:
            t("result.assets.description"),
          isDataPresent: resultSimulationData?.assetsInfo?.length,
          children: resultSimulationData?.assetsInfo?.length ? (
            <>
              <div style={{ display: "flex" }}>
                <MultiSelect
                  data={Array.from(
                    new Set(
                      resultSimulationData?.assetsInfo
                        ?.map((a) => a?.assetName)
                        ?.filter((name) => !isIgnoredAsset(name))
                    )
                  ).map((name) => ({ value: name, label: name }))}
                  value={enabledAssets}
                  onChange={setEnabledAssets}
                  label={t("result.assets.select.label")}
                  placeholder={t("result.assets.select.placeholder")}
                  clearable={false}
                  searchable
                  nothingFound={t("result.assets.select.nothingFound")}
                  style={{ maxWidth: 400 }}
                />
              </div>
              <Group position="center" align="center" mb="md">
                <SegmentedControl
                  value={assetView}
                  onChange={setAssetView}
                  data={[
                    { label: t("common.chart"), value: "chart" },
                    { label: t("common.table"), value: "table" },
                  ]}
                  color="seGreen.0"
                />
              </Group>
              <Group position="center" spacing="md" mb="md">
                <Text>{t("common.nominal")}</Text>
                <Switch
                  checked={isAssetChecked}
                  onChange={(e) => setIsAssetChecked(e.currentTarget.checked)}
                  color="seGreen.0"
                />
                <Text>{t("common.discounted")}</Text>
              </Group>


              {/* <span className="indicator-downloadable"> */}
              <AssetCost
                isAssetChecked={isAssetChecked}
                isAssetTable={assetView === "table"}
                resultSimulationData={resultSimulationData}
                enabledAssets={enabledAssets}
              />
              {/* </span> */}
            </>
          ) : (
            t("common.noDataAvailable")
          ),
        },
      ],
      [
        resultSimulationData,
        resultTimeSeries,
        isAssetChecked,
        billSummaryView,
        assetView,
        enabledAssets,
        showBillChart,
        isCashflowDiscounted,
        gridSharePct,
        onsiteSharePct,
        totalGenerationKWh,
        localUsePct,
        exportPct,
        timeSeriesLayer,
        projectDetails,
      ]
    );

    const handleScroll = (id) => {
      sectionRefs.current[id]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      setActiveId(id);
    };

    useEffect(() => {
      if (!showCompleteMicrogrid) {
        const contentEl = contentRef.current;
        const handleSpyScroll = () => {
          const scrollTop = contentEl.scrollTop + 100;
          for (const section of sections) {
            const el = sectionRefs.current[section.id];
            if (el) {
              const offsetTop = el.offsetTop;
              const offsetBottom = offsetTop + el.offsetHeight;
              if (scrollTop >= offsetTop && scrollTop < offsetBottom) {
                setActiveId(section.id);
                break;
              }
            }
          }
        };
        contentEl.addEventListener("scroll", handleSpyScroll);
        return () => contentEl.removeEventListener("scroll", handleSpyScroll);
      }
    }, [showCompleteMicrogrid]);

    return (
      <>
      <SeLoading loading={isDownloading} />
      <Box className={classes.container}>
        {showCompleteMicrogrid ? (
          <ScenarioTable
            handleClickCompare={handleClickCompare}
            showScenario={showScenario}
          />
        ) : (
          <>
            <Box
              className={classes.contentWrapper}
              ref={contentRef}
              style={{
                backgroundColor:
                  !isLightMode && "var(--se-background-alternative)",
              }}
            >
              
              {sections?.map((section) => {
                // Only modify the timeSeries section for lazy rendering
                // All other sections unchanged
                return (
                  <Box
                    key={section?.key || section?.id}
                    id={section?.id}
                    ref={(el) => (sectionRefs.current[section?.id] = el)}
                    className={[classes.section, "overide-result"]}
                    style={{
                      backgroundColor:
                        !isLightMode && "var(--se-background-alternative)",
                      display:
                        section?.id === "timeSeries" || section?.isDataPresent
                          ? "block"
                          : "none",
                    }}
                  >
                    <Title order={2} mb="xs">
                      {section?.label}
                    </Title>
                    {!(section?.id === "highlights" && iconKpiItems?.length > 0) && <br />}

                    {section?.id === "highlights" && iconKpiItems?.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: "2rem",
                          margin: "1rem 1rem 0",
                          justifyContent: "space-around",
                        }}
                      >
                        {iconKpiItems?.map((item, index) => (
                          <div key={index} style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                            <item.icon width={85} height={85} aria-label={item?.alt} />
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0 }}>
                              <p className="mb-0" style={{ margin: 0, lineHeight: 1.6, whiteSpace: "nowrap", fontSize: "16px" }}>
                                {item?.label}
                              </p>
                              <p className="mb-0" style={{ margin: 0, lineHeight: 1.6, fontSize: "14px" }}>
                                {item?.value}
                              </p>
                              {item?.value2 && (
                                <p className="mb-0" style={{ margin: 0, lineHeight: 1.6, fontSize: "14px" }}>
                                  {item?.value2}
                                </p>
                              )}
                              {item?.value3 && (
                                <p className="mb-0" style={{ margin: 0, lineHeight: 1.6, fontSize: "14px" }}>
                                  {item?.value3}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {section?.id === "highlights" && iconKpiItems?.length > 0 && <br />}
                    <Text
                      sx={{ fontSize: 22 }}
                      p="md"
                      dangerouslySetInnerHTML={{ __html: section?.description }}
                    />
                    <Paper
                      className={
                        section?.id === "highlights" || section?.id === "cashFlow"
                          ? "indicator-downloadable marrit"
                          : ""
                      }
                      p="md"
                      data-intro={
                        section?.id === "highlights"
                          ? t('result.kpisSummary')
                          : section?.id === "energyFlow"
                          ? t('result.energyFlowDiagram')
                          : section?.id === "energyBalance"
                          ? t('result.annualEnergyBreakdown')
                          : section?.id === "timeSeries"
                          ? t('result.operationsTimeSeries')
                          : section?.id === "cashFlow"
                          ? t('result.cashFlowChartTable')
                          : section?.id === "billSummary"
                          ? t('result.monthlyBillSummary')
                          : section?.id === "assets"
                          ? t('result.assetLifecycleCosts')
                          : undefined
                      }
                      data-position={
                      "top"
                      }
                    >
                      {section?.children}
                    </Paper>
                  </Box>
                );
              })}
            </Box>
            <Box
              className={classes.sidebar}
              style={{
                backgroundColor:
                  colorScheme === "dark"
                    ? "var(--se-background-alternative)"
                    : "transparent",
              }}
            >
              <Text fw={600} mb="md">
                {t("common.tableOfContents")}
              </Text>
              <Stack spacing={4}>
                {sections?.map((section) => (
                  <UnstyledButton
                    key={section?.key || section?.id}
                    className={cx(classes.tocItem, {
                      [classes.activeTocItem]: activeId === section?.id,
                    })}
                    onClick={() => handleScroll(section?.id)}
                    style={{
                      display:
                        section?.id === "timeSeries" || section?.isDataPresent
                          ? "block"
                          : "none",
                    }}
                  >
                    {section?.label}
                  </UnstyledButton>
                ))}
              </Stack>
            </Box>
            {/* SEConfirm`Modal for download confirmation */}
            <SEConfirmModal
              opened={showDownloadModal}
              onClose={() => setShowDownloadModal(false)}
              title={t("result.download.confirmTitle")}
              onOk={async () => {
                setShowDownloadModal(false);
                await handleDownloadSections();
              }}
              onCancel={() => setShowDownloadModal(false)}
              okText={t("common.yes")}
              cancelText={t("common.no")}
            />
          </>
        )}
      </Box>
      </>
    );
  }
);

TOCStickyLayout.propTypes = {
  resultTimeSeries: PropTypes.shape({
    load: PropTypes.array,
    pv: PropTypes.array,
    battery: PropTypes.array,
    grid: PropTypes.array,
    genset: PropTypes.array,
    thermalBefore: PropTypes.array,
    thermalAfter: PropTypes.array,
  }),
  resultSimulationData: PropTypes.shape({
    synthesis: PropTypes.object,
    energyBalance: PropTypes.object,
    cashflow: PropTypes.array,
    assetsInfo: PropTypes.array,
    energyFlow: PropTypes.object,
  }),
  showCompleteMicrogrid: PropTypes.bool,
  showScenario: PropTypes.object,
  handleClickCompare: PropTypes.func,
};

export default TOCStickyLayout;
